import { useMemo, useState } from 'react';
import { MONS } from '../mock/data.ts';
import type { SpeciesId, SpeedRow, SpeedTierTable as SpeedTierData } from '../types.ts';
import { CitationBadge } from './CitationBadge.tsx';
import { Sprite } from './Sprite.tsx';

// Speed-tier widget — interactive. Each row's nature / Scarf / boost stage is
// local state; we recompute final Speed client-side on every change. No API
// round-trips. The server-rendered `final_speed` is just the first paint.
//
// Formula (mainline level 50):
//   floor(((2*base + 31 + floor(ev/4)) * 0.5 + 5) * natureMod)
//   then * (scarf ? 1.5 : 1) * stageMult
//
// Stage multipliers come from the standard Pokemon stat-stage table.
//
// Known limitation: absolute speeds inherit the Champions /32-vs-mainline-/252
// data bug (Pikalytics modal `ev_spe: 32` is treated as 32 mainline EVs ≈ 8
// stat points, when it likely means max Speed in Champions' system). Relative
// comparisons within the table stay correct; absolute numbers are systematically
// under-stated until the Champions stat-math investigation lands.

const NATURES_SPE: Array<{ name: string; mod: number; label: string }> = [
  { name: 'Jolly', mod: 1.1, label: 'Jolly (+Spe)' },
  { name: 'Timid', mod: 1.1, label: 'Timid (+Spe)' },
  { name: 'Adamant', mod: 1.0, label: 'Adamant' },
  { name: 'Modest', mod: 1.0, label: 'Modest' },
  { name: 'Bold', mod: 1.0, label: 'Bold' },
  { name: 'Calm', mod: 1.0, label: 'Calm' },
  { name: 'Careful', mod: 1.0, label: 'Careful' },
  { name: 'Impish', mod: 1.0, label: 'Impish' },
  { name: 'Serious', mod: 1.0, label: 'Serious (neutral)' },
  { name: 'Brave', mod: 0.9, label: 'Brave (-Spe)' },
  { name: 'Quiet', mod: 0.9, label: 'Quiet (-Spe)' },
  { name: 'Sassy', mod: 0.9, label: 'Sassy (-Spe)' },
  { name: 'Relaxed', mod: 0.9, label: 'Relaxed (-Spe)' },
];
const NATURE_MOD = Object.fromEntries(NATURES_SPE.map((n) => [n.name, n.mod]));

// +N → ×(2+N)/2; −N → ×2/(2+N). Standard Pokemon stat-stage formula.
const STAGE_LABELS = [-6, -5, -4, -3, -2, -1, 0, 1, 2, 3, 4, 5, 6];
function stageMult(stage: number): number {
  if (stage >= 0) return (2 + stage) / 2;
  return 2 / (2 - stage);
}

function computeSpeed(
  base: number,
  evSpe: number,
  nature: string,
  scarf: boolean,
  stage: number,
  tailwind: boolean,
): number {
  const mod = NATURE_MOD[nature] ?? 1.0;
  const raw = Math.floor(((2 * base + 31 + Math.floor(evSpe / 4)) * 0.5 + 5) * mod);
  const scarfed = scarf ? Math.floor(raw * 1.5) : raw;
  const tailwinded = tailwind ? scarfed * 2 : scarfed;
  return Math.floor(tailwinded * stageMult(stage));
}

type RowState = {
  nature: string;
  scarf: boolean;
  stage: number;
  tailwind: boolean;
};

export function SpeedTierTable({
  data,
  highlightSpecies,
}: {
  data: SpeedTierData;
  // Optional list of species ids that should render with an accent left-rule
  // (used by the team dashboard to mark the user's team within the meta tier).
  highlightSpecies?: string[];
}) {
  // Hooks must be unconditional — declare BEFORE the early-return guard. We
  // tolerate an empty/missing data prop by initializing to safe defaults and
  // bailing on render.
  const safeRows = Array.isArray(data?.rows) ? data.rows : [];
  // Normalize once for fast inclusion checks during render.
  const highlightSet = useMemo(
    () => new Set((highlightSpecies ?? []).map(normalizeSpeciesId)),
    [highlightSpecies],
  );

  // Per-row local state, keyed by index in the original (sorted) rows array.
  // Initialized from the server's defaults so first paint matches the
  // server-computed numbers exactly.
  const [rowStates, setRowStates] = useState<Map<number, RowState>>(() => {
    const init = new Map<number, RowState>();
    safeRows.forEach((r, i) => {
      init.set(i, {
        nature: r.default_nature ?? 'Hardy',
        scarf: false,
        stage: 0,
        tailwind: false,
      });
    });
    return init;
  });

  // User row state — initialized from server-side input so when the agent
  // encodes "Garchomp +1 Dragon Dance" or "Scarf Sneasler", the widget shows
  // the boosted speed + position from first paint (not after a user click).
  const [meState, setMeState] = useState<RowState>({
    nature: data?.my_nature ?? 'Hardy',
    scarf: data?.my_scarf ?? false,
    stage: data?.my_stage ?? 0,
    tailwind: data?.my_tailwind ?? false,
  });

  // Local "show top N" filter — server always sends up to 30 rows by usage;
  // user can choose to show fewer without re-firing the tool. Defaults to 15.
  const [shownCount, setShownCount] = useState<number>(() => Math.min(15, safeRows.length || 15));

  // Compute live speeds for each row + user, then merge user row INTO the
  // meta-rows list so they sort together. When the user boosts their Speed
  // (Scarf, +1 stage, etc.), their row physically moves to its new ranked
  // position in the table — that's the whole point of an interactive widget.
  const computed = useMemo(() => {
    // If we have raw base + EV inputs, recompute fully. Otherwise, apply
    // Scarf and Stage multipliers to the server's static my_speed.
    const meSpeed =
      data?.my_base_speed !== undefined && data.my_ev_spe !== undefined
        ? computeSpeed(
            data.my_base_speed,
            data.my_ev_spe,
            meState.nature,
            meState.scarf,
            meState.stage,
            meState.tailwind,
          )
        : Math.floor(
            (data?.my_speed ?? 0) *
              (meState.scarf ? 1.5 : 1) *
              (meState.tailwind ? 2 : 1) *
              stageMult(meState.stage),
          );

    type CombinedRow = {
      isMe: boolean;
      species: string;
      speed: number;
      spread: string | null;
      state: RowState;
      onChange: (patch: Partial<RowState>) => void;
      canEdit: boolean;
      variantNature?: string | null;
      variantSpeed?: number | null;
      defaultNature?: string | null;
      key: string;
    };

    // Slice to the top N by usage (server's default sort order) BEFORE
    // computing speeds. This keeps "show top 5" predictable — the top 5 most-
    // used species, regardless of how their dynamic speed sorts after boosts.
    const visibleRows = safeRows.slice(0, shownCount);
    const combined: CombinedRow[] = visibleRows.map((row, i) => {
      const st = rowStates.get(i) ?? {
        nature: 'Hardy',
        scarf: false,
        stage: 0,
        tailwind: false,
      };
      const speed =
        row.base_speed !== undefined && row.default_ev_spe !== undefined
          ? computeSpeed(
              row.base_speed,
              row.default_ev_spe,
              st.nature,
              st.scarf,
              st.stage,
              st.tailwind,
            )
          : row.final_speed;
      return {
        isMe: false,
        species: row.species,
        speed,
        spread: row.common_spread,
        state: st,
        onChange: (patch: Partial<RowState>) =>
          setRowStates((prev) => {
            const next = new Map(prev);
            next.set(i, { ...(next.get(i) as RowState), ...patch });
            return next;
          }),
        canEdit: row.base_speed !== undefined && row.default_ev_spe !== undefined,
        variantNature: row.variant_nature,
        variantSpeed: row.variant_final_speed,
        defaultNature: row.default_nature,
        key: `${row.species}-${i}`,
      };
    });

    // Insert the user's row only if we have a species. It interleaves into
    // the sort like any other row.
    if (data?.my_species) {
      combined.push({
        isMe: true,
        species: data.my_species,
        speed: meSpeed,
        spread: null,
        state: meState,
        onChange: (patch: Partial<RowState>) => setMeState((p) => ({ ...p, ...patch })),
        canEdit: true,
        key: `__me__${data.my_species}`,
      });
    }

    combined.sort((a, b) => b.speed - a.speed);
    return { meSpeed, combined };
  }, [data, safeRows, rowStates, meState, shownCount]);

  // Render guard — early return AFTER all hooks are declared.
  if (safeRows.length === 0) {
    return (
      <div className="mono" style={{ color: 'var(--fg-4)', fontSize: 11, opacity: 0.6 }}>
        speed tier table · missing or empty rows
      </div>
    );
  }

  return (
    <div className="vb-card">
      <div className="vb-card-header">
        <span
          className="caps"
          style={{ color: 'var(--fg-3)', fontSize: 10, letterSpacing: '0.08em' }}
        >
          speed tiers · me at {computed.meSpeed}
        </span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <span
              className="caps mono"
              style={{ color: 'var(--fg-3)', fontSize: 9, letterSpacing: '0.08em' }}
            >
              show top
            </span>
            <select
              value={shownCount}
              onChange={(e) => setShownCount(Number(e.target.value))}
              style={{
                background: 'var(--bg-2)',
                color: 'var(--fg-1)',
                border: '1px solid var(--line-1)',
                borderRadius: 3,
                padding: '1px 4px',
                fontSize: 11,
                fontFamily: 'var(--font-mono)',
              }}
            >
              {[5, 10, 15, 20, 25, 30]
                .filter((n) => n <= safeRows.length || n === 5)
                .map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
            </select>
          </label>
          <CitationBadge source="pikalytics" />
        </div>
      </div>

      {/* Unified rows — user row + meta rows interleaved by current Speed.
          When the user boosts their own Speed (Scarf / stage), their row
          physically moves to its new ranked position. */}
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        {computed.combined.map((r) => (
          <SpeedRowDisplay
            key={r.key}
            species={r.species as SpeciesId}
            speed={r.speed}
            comparisonSpeed={computed.meSpeed}
            spread={r.spread}
            state={r.state}
            onChange={r.onChange}
            isMe={r.isMe}
            inTeam={highlightSet.has(normalizeSpeciesId(r.species))}
            canEdit={r.canEdit}
            variantNature={r.variantNature}
            variantSpeed={r.variantSpeed}
            defaultNature={r.defaultNature}
          />
        ))}
      </div>
    </div>
  );
}

// Normalize species ids so user-team strings ("sneasler") match Pikalytics
// labels ("Sneasler", "Sneasler-Mega") via case-insensitive base-name compare.
function normalizeSpeciesId(s: string): string {
  return s
    .toLowerCase()
    .replace(/-mega(-[xy])?$/i, '')
    .replace(/[^a-z0-9]/g, '');
}

function SpeedRowDisplay({
  species,
  speed,
  comparisonSpeed,
  spread,
  state,
  onChange,
  isMe,
  inTeam,
  canEdit,
  variantNature,
  variantSpeed,
  defaultNature,
}: {
  species: SpeciesId;
  speed: number;
  comparisonSpeed: number;
  spread: string | null;
  state: RowState;
  onChange: (patch: Partial<RowState>) => void;
  isMe: boolean;
  inTeam: boolean;
  canEdit: boolean;
  variantNature?: string | null;
  variantSpeed?: number | null;
  defaultNature?: string | null;
}) {
  const [expanded, setExpanded] = useState(false);
  const outpaces = !isMe && speed > comparisonSpeed;
  const ties = !isMe && speed === comparisonSpeed;
  const outcome = isMe ? '' : outpaces ? '▲' : ties ? '=' : '▼';
  const outcomeColor = outpaces ? 'var(--neg)' : ties ? 'var(--warn)' : 'var(--pos)';

  // Suffix shown in the row's metadata line: which nature is active + scarf/boost
  // diffs from default. Keeps the row scannable without expanding.
  const tweaks: string[] = [];
  if (state.scarf) tweaks.push('Scarf');
  if (state.tailwind) tweaks.push('Tailwind');
  if (state.stage !== 0) tweaks.push(state.stage > 0 ? `+${state.stage}` : `${state.stage}`);

  return (
    <div
      style={{
        borderTop: '1px solid var(--line-1)',
        background: isMe ? 'var(--accent-bg)' : 'transparent',
        borderLeft: inTeam && !isMe ? '2px solid var(--accent-1)' : undefined,
      }}
    >
      <button
        type="button"
        onClick={() => canEdit && setExpanded((e) => !e)}
        disabled={!canEdit}
        style={{
          all: 'unset',
          cursor: canEdit ? 'pointer' : 'default',
          display: 'grid',
          gridTemplateColumns: '40px 1fr auto auto 20px',
          gap: 10,
          alignItems: 'center',
          padding: '6px 12px',
          width: '100%',
          boxSizing: 'border-box',
        }}
        title={canEdit ? 'Click to tweak alignment / Scarf / boost' : ''}
      >
        <Sprite species={species} size={32} />
        <div style={{ minWidth: 0 }}>
          <div
            style={{
              color: isMe ? 'var(--accent-2)' : 'var(--fg-1)',
              fontWeight: isMe ? 600 : 400,
              fontSize: 13,
            }}
          >
            {MONS[species]?.name ?? species}
            {isMe && (
              <span
                className="caps mono"
                style={{
                  marginLeft: 6,
                  color: 'var(--accent-2)',
                  fontSize: 9,
                  letterSpacing: '0.1em',
                }}
              >
                you
              </span>
            )}
          </div>
          <div className="mono" style={{ color: 'var(--fg-3)', fontSize: 10 }}>
            {state.nature}
            {spread && <span style={{ color: 'var(--fg-4)' }}> · {spread}</span>}
            {/* Heuristic-variant nature shown alongside modal so the user
                sees "the obvious alternative" without expanding. Only shown
                when state.nature still matches the default modal (i.e. user
                hasn't overridden it). */}
            {variantNature && variantSpeed != null && state.nature === defaultNature && (
              <span style={{ color: 'var(--fg-4)' }}>
                {' '}
                · or {variantNature} {variantSpeed}
              </span>
            )}
            {tweaks.length > 0 && (
              <span style={{ color: 'var(--accent-2)' }}> · {tweaks.join(' · ')}</span>
            )}
          </div>
        </div>
        <div
          className="mono tabular"
          style={{ color: 'var(--fg-1)', fontSize: 13, fontWeight: 500 }}
        >
          {speed}
        </div>
        <span
          style={{
            color: outcomeColor,
            fontFamily: 'var(--font-mono)',
            fontSize: 13,
            width: 12,
            textAlign: 'center',
          }}
          title={outpaces ? 'outpaces you' : ties ? 'ties' : isMe ? 'you' : 'outpaced'}
        >
          {outcome}
        </span>
        <span className="mono" style={{ color: 'var(--fg-4)', fontSize: 10 }}>
          {canEdit ? (expanded ? '▾' : '▸') : ''}
        </span>
      </button>

      {expanded && canEdit && (
        <div
          style={{
            padding: '8px 12px 10px 60px',
            display: 'flex',
            gap: 12,
            alignItems: 'center',
            flexWrap: 'wrap',
            fontSize: 11,
          }}
        >
          <label style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            <span
              className="caps mono"
              style={{ color: 'var(--fg-3)', fontSize: 9, letterSpacing: '0.08em' }}
            >
              nature
            </span>
            <select
              value={state.nature}
              onChange={(e) => onChange({ nature: e.target.value })}
              style={{
                background: 'var(--bg-2)',
                color: 'var(--fg-1)',
                border: '1px solid var(--line-1)',
                borderRadius: 3,
                padding: '2px 4px',
                fontSize: 11,
                fontFamily: 'var(--font-mono)',
              }}
            >
              {NATURES_SPE.map((n) => (
                <option key={n.name} value={n.name}>
                  {n.label}
                </option>
              ))}
            </select>
          </label>

          <label style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            <input
              type="checkbox"
              checked={state.scarf}
              onChange={(e) => onChange({ scarf: e.target.checked })}
            />
            <span
              className="caps mono"
              style={{ color: 'var(--fg-3)', fontSize: 9, letterSpacing: '0.08em' }}
            >
              choice scarf
            </span>
            <span style={{ color: 'var(--fg-4)' }}>(×1.5)</span>
          </label>

          <label style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            <input
              type="checkbox"
              checked={state.tailwind}
              onChange={(e) => onChange({ tailwind: e.target.checked })}
            />
            <span
              className="caps mono"
              style={{ color: 'var(--fg-3)', fontSize: 9, letterSpacing: '0.08em' }}
            >
              tailwind
            </span>
            <span style={{ color: 'var(--fg-4)' }}>(×2)</span>
          </label>

          <label style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            <span
              className="caps mono"
              style={{ color: 'var(--fg-3)', fontSize: 9, letterSpacing: '0.08em' }}
            >
              stage
            </span>
            <select
              value={state.stage}
              onChange={(e) => onChange({ stage: Number(e.target.value) })}
              style={{
                background: 'var(--bg-2)',
                color: 'var(--fg-1)',
                border: '1px solid var(--line-1)',
                borderRadius: 3,
                padding: '2px 4px',
                fontSize: 11,
                fontFamily: 'var(--font-mono)',
              }}
            >
              {STAGE_LABELS.map((s) => (
                <option key={s} value={s}>
                  {s > 0 ? `+${s}` : s} ({stageMult(s).toFixed(2)}×)
                </option>
              ))}
            </select>
          </label>
        </div>
      )}
    </div>
  );
}

// Suppress unused-import warning if SpeedRow isn't referenced after refactor.
type _Unused = SpeedRow;
